import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { BasicTooltip, Icon, InputWithIcon, BackdropLoader } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { getSubscriptionTermLength, getSubscriptionTermSortRank } from 'src/constants/subscriptionTerm';
import { dateToFormat } from 'src/utils/date';
import { getSubscriptionStatusTagSx, SUBSCRIPTION_STATUS_CANCELED } from './initialState';
import { formatScheduleDate } from './ScheduleChange/scheduleChangeDates';
import { getPendingCancellationDate, getPendingCancellationScheduler } from './ScheduleChange/scheduleChangeTypes';
import useStyles from './styles';
import { ChangeStatusModal } from './ChangeStatusModal';
import { ManagePricingsModal } from './ManagePricingsModal';
import { SubscriptionModal } from './SubscriptionModal';
import { SubscriptionsFilterMenu } from './SubscriptionsFilterMenu';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;
const DEFAULT_STATUS_FILTER = ['Active', 'Draft', 'Paused'];
const EMPTY_SUBSCRIPTIONS = [];
function formatPriceAmount(val) {
  return typeof val === 'number' ? "$".concat(Number(val).toFixed(2)) : String(val !== null && val !== void 0 ? val : '');
}
function formatSubscriptionDate(date) {
  if (!date || date === '0001-01-01') return '';
  const formatted = dateToFormat(date);
  return formatted === 'N/A' ? '' : formatted;
}
function getTieredMinPrice(p) {
  var _table$tierPrice;
  if (typeof p.minimumPrice === 'number' && !Number.isNaN(p.minimumPrice)) return p.minimumPrice;
  const table = p.priceTableResponse;
  if (!table) return null;
  if (typeof table.minimumPrice === 'number' && !Number.isNaN(table.minimumPrice)) return table.minimumPrice;
  const amounts = ((_table$tierPrice = table.tierPrice) !== null && _table$tierPrice !== void 0 ? _table$tierPrice : []).map(t => t.amount).filter(n => typeof n === 'number');
  return amounts.length > 0 ? Math.min(...amounts) : null;
}
export const SubscriptionsTab = observer(() => {
  var _store$stripe$subscri, _subscriptions$find, _store$stripe$priceDa, _selectedSubscription;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const subscriptions = (_store$stripe$subscri = store.stripe.subscriptionsPaged) !== null && _store$stripe$subscri !== void 0 ? _store$stripe$subscri : EMPTY_SUBSCRIPTIONS;
  useEffect(() => {
    if (!store.stripe.subscriptionsListLoaded) {
      store.stripe.getSubscriptionsLoadAllAsync.run({
        pageSize: 100
      });
    }
  }, [store.stripe]);
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    orderBy = _useState2[0],
    setOrderBy = _useState2[1];
  const _useState3 = useState('asc'),
    _useState4 = _slicedToArray(_useState3, 2),
    order = _useState4[0],
    setOrder = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    page = _useState6[0],
    setPage = _useState6[1];
  const _useState7 = useState(DEFAULT_ROWS_PER_PAGE),
    _useState8 = _slicedToArray(_useState7, 2),
    rowsPerPage = _useState8[0],
    setRowsPerPage = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    selected = _useState0[0],
    setSelected = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    changeStatusModalOpen = _useState10[0],
    setChangeStatusModalOpen = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    managePricingsModalOpen = _useState12[0],
    setManagePricingsModalOpen = _useState12[1];
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    subscriptionModalOpen = _useState14[0],
    setSubscriptionModalOpen = _useState14[1];
  const _useState15 = useState('create'),
    _useState16 = _slicedToArray(_useState15, 2),
    subscriptionModalMode = _useState16[0],
    setSubscriptionModalMode = _useState16[1];
  const _useState17 = useState(''),
    _useState18 = _slicedToArray(_useState17, 2),
    subscriptionToEditId = _useState18[0],
    setSubscriptionToEditId = _useState18[1];
  const _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isSavingSubscription = _useState20[0],
    setSavingSubscription = _useState20[1];
  const _useState21 = useState(''),
    _useState22 = _slicedToArray(_useState21, 2),
    searchQuery = _useState22[0],
    setSearchQuery = _useState22[1];
  const _useState23 = useState([]),
    _useState24 = _slicedToArray(_useState23, 2),
    filterScope = _useState24[0],
    setFilterScope = _useState24[1];
  const _useState25 = useState(DEFAULT_STATUS_FILTER),
    _useState26 = _slicedToArray(_useState25, 2),
    filterStatus = _useState26[0],
    setFilterStatus = _useState26[1];
  const _useState27 = useState([]),
    _useState28 = _slicedToArray(_useState27, 2),
    filterTerm = _useState28[0],
    setFilterTerm = _useState28[1];
  const _useState29 = useState([]),
    _useState30 = _slicedToArray(_useState29, 2),
    filterPricings = _useState30[0],
    setFilterPricings = _useState30[1];
  const subscriptionToEdit = subscriptionToEditId ? (_subscriptions$find = subscriptions.find(row => row.id === subscriptionToEditId)) !== null && _subscriptions$find !== void 0 ? _subscriptions$find : null : null;
  useEffect(() => {
    if (!subscriptionModalOpen || !subscriptionToEditId || subscriptionToEdit) return;
    setSubscriptionModalOpen(false);
    setSubscriptionToEditId('');
  }, [subscriptionModalOpen, subscriptionToEditId, subscriptionToEdit]);
  const priceDashboard = (_store$stripe$priceDa = store.stripe.priceDashboard) !== null && _store$stripe$priceDa !== void 0 ? _store$stripe$priceDa : [];
  const uniquePricingsFromSubscriptions = (() => {
    const seen = new Set();
    const list = [];
    for (let i = 0; i < priceDashboard.length; i++) {
      var _ref, _product$productName, _product$priceRespons;
      const product = priceDashboard[i];
      const productName = (_ref = product && ((_product$productName = product.productName) !== null && _product$productName !== void 0 ? _product$productName : product.name)) !== null && _ref !== void 0 ? _ref : '';
      const priceResponse = (_product$priceRespons = product.priceResponse) !== null && _product$priceRespons !== void 0 ? _product$priceRespons : [];
      const arr = Array.isArray(priceResponse) ? priceResponse : [];
      for (let j = 0; j < arr.length; j++) {
        var _ref2, _ref3, _p$priceId;
        const p = arr[j];
        const id = (_ref2 = p && ((_ref3 = (_p$priceId = p.priceId) !== null && _p$priceId !== void 0 ? _p$priceId : p.id) !== null && _ref3 !== void 0 ? _ref3 : p.code)) !== null && _ref2 !== void 0 ? _ref2 : '';
        if (id && !seen.has(id)) {
          var _ref4, _ref5, _p$name;
          seen.add(id);
          const priceName = (_ref4 = p && ((_ref5 = (_p$name = p.name) !== null && _p$name !== void 0 ? _p$name : p.priceName) !== null && _ref5 !== void 0 ? _ref5 : p.productName)) !== null && _ref4 !== void 0 ? _ref4 : id;
          const label = productName ? "".concat(productName, " \u2013 ").concat(priceName) : priceName;
          list.push({
            id,
            label
          });
        }
      }
    }
    return list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  })();
  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredBySearch = !searchLower ? subscriptions : subscriptions.filter(s => (s.companyName || '').toLowerCase().includes(searchLower) || (s.companyHolderEmail || '').toLowerCase().includes(searchLower) || (s.scope || '').toLowerCase().includes(searchLower) || (s.status || '').toLowerCase().includes(searchLower));
  const filteredByColumns = filterScope.length === 0 && filterStatus.length === 0 && filterTerm.length === 0 && filterPricings.length === 0 ? filteredBySearch : filteredBySearch.filter(s => {
    if (filterScope.length > 0 && !filterScope.includes(s.scope)) return false;
    if (filterStatus.length > 0 && !filterStatus.includes(s.status)) return false;
    if (filterTerm.length > 0 && !filterTerm.includes(s.term)) return false;
    if (filterPricings.length > 0) {
      const subscriptionPriceIds = new Set((s.prices || []).map(p => {
        var _ref6, _p$id;
        return (_ref6 = (_p$id = p.id) !== null && _p$id !== void 0 ? _p$id : p.priceId) !== null && _ref6 !== void 0 ? _ref6 : p.code;
      }).filter(Boolean));
      const hasSelectedPricing = filterPricings.some(pid => subscriptionPriceIds.has(pid));
      if (!hasSelectedPricing) return false;
    }
    return true;
  });
  const sortKey = orderBy === 'account' ? 'companyName' : orderBy === 'scope' ? 'scope' : orderBy === 'properties' ? 'propertiesCount' : orderBy === 'units' ? 'activeUnitsCount' : orderBy === 'billableUnits' ? 'billableUnitsCount' : orderBy === 'startDate' ? 'startDate' : orderBy === 'status' ? 'status' : orderBy;
  const sortedRows = orderBy == null ? [...filteredByColumns] : [...filteredByColumns].sort((a, b) => {
    const getSortValue = row => {
      var _row$prices$length, _row$prices;
      if (orderBy === 'pricings') return (_row$prices$length = (_row$prices = row.prices) === null || _row$prices === void 0 ? void 0 : _row$prices.length) !== null && _row$prices$length !== void 0 ? _row$prices$length : 0;
      if (orderBy === 'term') return getSubscriptionTermSortRank(row.term);
      return row[sortKey];
    };
    const aVal = getSortValue(a);
    const bVal = getSortValue(b);
    const cmp = typeof aVal === 'string' || typeof bVal === 'string' ? String(aVal !== null && aVal !== void 0 ? aVal : '').localeCompare(String(bVal !== null && bVal !== void 0 ? bVal : '')) : (aVal !== null && aVal !== void 0 ? aVal : 0) - (bVal !== null && bVal !== void 0 ? bVal : 0);
    return order === 'asc' ? cmp : -cmp;
  });
  const start = page * rowsPerPage;
  const paginatedRows = sortedRows.slice(start, start + rowsPerPage);
  const handleSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelected(paginatedRows.map(r => r.id));
    } else {
      setSelected([]);
    }
  };
  const handleSelectOne = id => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = e => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  const handleClearSelection = () => setSelected([]);
  const openChangeStatusModal = () => setChangeStatusModalOpen(true);
  const closeChangeStatusModal = () => setChangeStatusModalOpen(false);
  const openManagePricingsModal = () => setManagePricingsModalOpen(true);
  const closeManagePricingsModal = () => setManagePricingsModalOpen(false);
  const openSubscriptionModal = function (mode) {
    var _subscription$id;
    let subscription = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    setSubscriptionModalMode(mode);
    setSubscriptionToEditId((_subscription$id = subscription === null || subscription === void 0 ? void 0 : subscription.id) !== null && _subscription$id !== void 0 ? _subscription$id : '');
    setSubscriptionModalOpen(true);
  };
  const closeSubscriptionModal = () => {
    setSubscriptionModalOpen(false);
    setSubscriptionToEditId('');
  };
  const handleSaveSubscription = async data => {
    if (subscriptionModalMode === 'create' && data) {
      setSavingSubscription(true);
      try {
        var _data$trialDays, _data$trialName;
        await store.stripe.createSubscriptionAsync.run({
          companyId: data.companyId || '',
          scope: data.scope || 'Account',
          startDate: data.startDate || new Date().toISOString().slice(0, 10),
          term: data.term,
          priceIds: (data.pricings || []).map(p => p.id || p.code).filter(Boolean),
          propertyIds: data.propertyIds || [],
          trialDays: (_data$trialDays = data.trialDays) !== null && _data$trialDays !== void 0 ? _data$trialDays : null,
          trialName: (_data$trialName = data.trialName) !== null && _data$trialName !== void 0 ? _data$trialName : null
        });

        // Coupons attach to the account, so they can only be applied once the account has been
        // committed to; a failure here leaves the subscription in place and is surfaced on its own.
        // Each queued code carries its own target: the account, or one property within it.
        for (const queued of (_data$couponCodes = data.couponCodes) !== null && _data$couponCodes !== void 0 ? _data$couponCodes : []) {
          var _data$couponCodes, _queued$propertyId;
          await store.stripe.applyCouponAsync.run({
            code: queued.code,
            companyId: data.companyId || '',
            propertyId: (_queued$propertyId = queued.propertyId) !== null && _queued$propertyId !== void 0 ? _queued$propertyId : null
          });
          if (store.stripe.applyCouponAsync.isError) break;
        }
        await store.stripe.getSubscriptionsLoadAllAsync.run({
          pageSize: 100
        });
        closeSubscriptionModal();
      } catch (err) {
        console.error('Create subscription failed:', err);
      } finally {
        setSavingSubscription(false);
      }
    } else if (subscriptionModalMode === 'edit' && data) {
      try {
        await store.stripe.getSubscriptionsLoadAllAsync.run({
          pageSize: 100
        });
      } finally {
        closeSubscriptionModal();
      }
    } else {
      closeSubscriptionModal();
    }
  };
  const handleSavePricings = async () => {
    try {
      await store.stripe.getSubscriptionsLoadAllAsync.run({
        pageSize: 100
      });
    } catch (err) {
      console.error('Refetch subscriptions failed:', err);
    }
  };
  const handleApplyStatus = async () => {
    setSelected([]);
    closeChangeStatusModal();
    try {
      await store.stripe.getSubscriptionsLoadAllAsync.run({
        pageSize: 100
      });
    } catch (err) {
      console.error('Refetch subscriptions failed:', err);
    }
  };
  const hasSelection = selected.length > 0;
  const selectedSubscriptions = subscriptions.filter(s => selected.includes(s.id));
  const hasCanceledSelection = selectedSubscriptions.some(s => s.status === SUBSCRIPTION_STATUS_CANCELED);
  const renderStatusCell = row => {
    const pendingCancellationDate = getPendingCancellationDate(row.scheduledChanges);
    const pendingCancellationScheduler = getPendingCancellationScheduler(row.scheduledChanges);
    const terminationDate = row.status === SUBSCRIPTION_STATUS_CANCELED ? formatScheduleDate(row.terminationDate) : '';
    const statusChip = /*#__PURE__*/React.createElement(Chip, {
      label: row.status,
      size: "small",
      sx: getSubscriptionStatusTagSx(row.status)
    });
    return /*#__PURE__*/React.createElement(Box, {
      className: classes.statusCell
    }, terminationDate ? /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t('subscriptions.scheduleChange.cancel.canceled_row_tooltip', {
        date: terminationDate
      }),
      placement: "top"
    }, statusChip) : statusChip, !!pendingCancellationDate && /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t(pendingCancellationScheduler ? 'subscriptions.scheduleChange.cancel.row_tooltip_by' : 'subscriptions.scheduleChange.cancel.row_tooltip', {
        date: formatScheduleDate(pendingCancellationDate),
        name: pendingCancellationScheduler
      }),
      placement: "top"
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.pendingChangeIcon
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "alert",
      fontSize: "small",
      color: "warning"
    }))));
  };
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsRoot,
    style: hasSelection ? {
      paddingBottom: 56
    } : undefined
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: store.stripe.getSubscriptionsLoadAllAsync.inProgress
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsToolbarColumn
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsToolbarRow
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsToolbarLeft
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    size: "small",
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    placeholder: "Search by company or email",
    fullWidth: true,
    value: searchQuery,
    onChange: handleSearchChange
  })), /*#__PURE__*/React.createElement(SubscriptionsFilterMenu, {
    filterScope: filterScope,
    filterStatus: filterStatus,
    filterTerm: filterTerm,
    filterPricings: filterPricings,
    availablePricings: uniquePricingsFromSubscriptions,
    onScopeChange: v => {
      setFilterScope(v);
      setPage(0);
    },
    onStatusChange: v => {
      setFilterStatus(v);
      setPage(0);
    },
    onTermChange: v => {
      setFilterTerm(v);
      setPage(0);
    },
    onPricingsChange: v => {
      setFilterPricings(v);
      setPage(0);
    },
    onApply: () => setPage(0)
  }), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "infoIcon",
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "info"
    }),
    label: "Invoice day: on the 5th of the month"
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    onClick: () => openSubscriptionModal('create')
  }, "New subscription")), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    className: classes.subscriptionsCount
  }, "Showing", ' ', /*#__PURE__*/React.createElement(Typography, {
    component: "span",
    variant: "body2",
    fontWeight: 700,
    color: "text.primary"
  }, filteredByColumns.length), ' ', "of ", subscriptions.length, " subscriptions")), /*#__PURE__*/React.createElement(Paper, {
    variant: "outlined",
    className: classes.subscriptionsTablePaper
  }, subscriptions.length > 0 && filteredByColumns.length === 0 ? /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsTableEmpty
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No subscriptions match your search and filters.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TableContainer, {
    className: classes.subscriptionsTableContainer
  }, /*#__PURE__*/React.createElement(Table, {
    stickyHeader: true,
    size: "small",
    "aria-label": "subscriptions table"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    padding: "checkbox",
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(Checkbox, {
    indeterminate: selected.length > 0 && selected.length < paginatedRows.length,
    checked: paginatedRows.length > 0 && selected.length === paginatedRows.length,
    onChange: handleSelectAll,
    size: "small"
  })), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'account',
    direction: orderBy === 'account' ? order : 'asc',
    onClick: () => handleSort('account')
  }, "Account")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'scope',
    direction: orderBy === 'scope' ? order : 'asc',
    onClick: () => handleSort('scope')
  }, "Scope")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'properties',
    direction: orderBy === 'properties' ? order : 'asc',
    onClick: () => handleSort('properties')
  }, "Properties")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'units',
    direction: orderBy === 'units' ? order : 'asc',
    onClick: () => handleSort('units')
  }, "Active units")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'billableUnits',
    direction: orderBy === 'billableUnits' ? order : 'asc',
    onClick: () => handleSort('billableUnits')
  }, "Billable units")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'startDate',
    direction: orderBy === 'startDate' ? order : 'asc',
    onClick: () => handleSort('startDate')
  }, "Start date")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'term',
    direction: orderBy === 'term' ? order : 'asc',
    onClick: () => handleSort('term')
  }, "Term")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'renewalDate',
    direction: orderBy === 'renewalDate' ? order : 'asc',
    onClick: () => handleSort('renewalDate')
  }, "Renewal date")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'status',
    direction: orderBy === 'status' ? order : 'asc',
    onClick: () => handleSort('status')
  }, "Status")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'pricings',
    direction: orderBy === 'pricings' ? order : 'asc',
    onClick: () => handleSort('pricings')
  }, "Pricings")), /*#__PURE__*/React.createElement(TableCell, {
    className: classes.tableHeadCell,
    align: "center"
  }, "Actions"))), /*#__PURE__*/React.createElement(TableBody, null, paginatedRows.map(row => {
    var _row$propertiesCount, _row$activeUnitsCount, _row$billableUnitsCou, _row$prices2;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: row.id,
      hover: true,
      className: classes.tableBodyRow,
      selected: selected.includes(row.id)
    }, /*#__PURE__*/React.createElement(TableCell, {
      padding: "checkbox"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: selected.includes(row.id),
      onChange: () => handleSelectOne(row.id),
      size: "small"
    })), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      fontWeight: 600,
      color: "text.primary"
    }, row.companyName), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, row.companyHolderEmail ? row.companyHolderEmail : 'N/A'))), /*#__PURE__*/React.createElement(TableCell, null, row.scope), /*#__PURE__*/React.createElement(TableCell, null, (_row$propertiesCount = row.propertiesCount) !== null && _row$propertiesCount !== void 0 ? _row$propertiesCount : 0), /*#__PURE__*/React.createElement(TableCell, null, (_row$activeUnitsCount = row.activeUnitsCount) !== null && _row$activeUnitsCount !== void 0 ? _row$activeUnitsCount : 0), /*#__PURE__*/React.createElement(TableCell, null, (_row$billableUnitsCou = row.billableUnitsCount) !== null && _row$billableUnitsCou !== void 0 ? _row$billableUnitsCou : 0), /*#__PURE__*/React.createElement(TableCell, null, formatSubscriptionDate(row.startDate)), /*#__PURE__*/React.createElement(TableCell, null, getSubscriptionTermLength(row.term)), /*#__PURE__*/React.createElement(TableCell, null, formatSubscriptionDate(row.renewalDate)), /*#__PURE__*/React.createElement(TableCell, null, renderStatusCell(row), row.trialDays ? /*#__PURE__*/React.createElement(BasicTooltip, {
      title: row.trialEndDate ? "No invoices until ".concat(dateToFormat(row.trialEndDate)) : 'Free trial'
    }, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      label: "Trial ".concat(row.trialDays, "d"),
      sx: {
        marginLeft: '6px'
      }
    })) : null), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.pricingsCell
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.pricingsStack
    }, !((_row$prices2 = row.prices) !== null && _row$prices2 !== void 0 && _row$prices2.length) ? /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, "\u2014") : row.prices.map(p => {
      var _p$productName;
      const isTiered = (p.priceType || '').toLowerCase() === 'tiered';
      const minPrice = isTiered ? getTieredMinPrice(p) : null;
      const amount = isTiered && minPrice != null ? "Min ".concat(formatPriceAmount(minPrice)) : formatPriceAmount(p.fixedPrice);
      return /*#__PURE__*/React.createElement(Box, {
        key: p.id,
        className: classes.pricingCard
      }, /*#__PURE__*/React.createElement(Box, {
        className: classes.pricingCardContent
      }, /*#__PURE__*/React.createElement(Box, {
        flex: 1,
        minWidth: 0
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        fontWeight: 600,
        sx: {
          lineHeight: 1.25
        }
      }, p.priceName), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "text.secondary",
        sx: {
          lineHeight: 1.2
        }
      }, (_p$productName = p.productName) !== null && _p$productName !== void 0 ? _p$productName : p.productId, " - ", p.priceType)), /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        fontWeight: 600,
        className: classes.pricingAmount
      }, amount)));
    }))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.actionsCell,
      align: "center"
    }, /*#__PURE__*/React.createElement(IconButton, {
      "aria-label": "Edit subscription",
      onClick: () => openSubscriptionModal('edit', row)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }))));
  })))), /*#__PURE__*/React.createElement(TablePagination, {
    component: "div",
    count: sortedRows.length,
    page: page,
    onPageChange: handleChangePage,
    rowsPerPage: rowsPerPage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    className: classes.tablePagination
  }))), hasSelection && /*#__PURE__*/React.createElement(Paper, {
    className: classes.selectionBar,
    elevation: 2
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.selectionBarLeft
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleClearSelection,
    "aria-label": "Clear selection"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, selected.length, " items selected")), /*#__PURE__*/React.createElement(Box, {
    className: classes.selectionBarRight
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "tag"
    }),
    onClick: openManagePricingsModal
  }, "Manage pricings"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }),
    onClick: openChangeStatusModal
  }, "Change status"))), /*#__PURE__*/React.createElement(ChangeStatusModal, {
    isOpen: changeStatusModalOpen,
    onClose: closeChangeStatusModal,
    initialStatus: (_selectedSubscription = selectedSubscriptions[0]) === null || _selectedSubscription === void 0 ? void 0 : _selectedSubscription.status,
    onApply: handleApplyStatus,
    selectedSubscriptionIds: selected,
    hasCanceledSelection: hasCanceledSelection
  }), /*#__PURE__*/React.createElement(ManagePricingsModal, {
    isOpen: managePricingsModalOpen,
    onClose: closeManagePricingsModal,
    selectedIds: selected,
    subscriptions: subscriptions,
    onSave: handleSavePricings
  }), /*#__PURE__*/React.createElement(SubscriptionModal, {
    isOpen: subscriptionModalOpen,
    onClose: closeSubscriptionModal,
    mode: subscriptionModalMode,
    subscription: subscriptionToEdit,
    onSave: handleSaveSubscription,
    isSubmitting: isSavingSubscription
  }));
});